import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PORTALS = {
  admin: { eyebrow: "Restricted · Back Office", loginPath: "/admin/login", forgotPath: "/admin/forgot-password" },
  borrower: { eyebrow: "Borrower Portal", loginPath: "/borrower", forgotPath: "/borrower/forgot-password" },
  broker: { eyebrow: "Broker / Agent Desk", loginPath: "/broker", forgotPath: "/broker/forgot-password" },
};

export default function ResetPassword({ portal = "admin" }) {
  const cfg = PORTALS[portal] || PORTALS.admin;
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav(cfg.loginPath, { replace: true }), 2200);
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || "Something went wrong.");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-[#B8860B] font-bold mb-3">
            <Lock size={11} /> {cfg.eyebrow}
          </div>
          <h1 className="text-3xl font-serif tracking-tight">Set New Password</h1>
        </div>

        {!token ? (
          <div className="bg-[#13130E] border border-[#9E2A2B]/40 rounded-md p-7 text-center text-[#FF8585] text-sm" data-testid="reset-no-token">
            This reset link is missing its token. Please request a new link from the sign-in page.
            <div className="mt-4"><Link to={cfg.forgotPath} className="text-[#E4C97A] underline text-[12px]">Request a new link</Link></div>
          </div>
        ) : done ? (
          <div className="bg-[#13130E] border border-[#2A2A22] rounded-md p-7 text-center" data-testid="reset-done">
            <CheckCircle2 size={34} className="text-[#3DBB8E] mx-auto mb-3" />
            <p className="text-sm">Password updated. Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#13130E] border border-[#2A2A22] rounded-md p-7 shadow-2xl">
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A] block mb-1.5">New Password</label>
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" required
              className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[#ECECE4] focus:border-[#B8860B] focus:outline-none transition-colors"
              data-testid="reset-password-input" />
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A] block mt-4 mb-1.5">Confirm Password</label>
            <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" required
              className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[#ECECE4] focus:border-[#B8860B] focus:outline-none transition-colors"
              data-testid="reset-password-confirm" />
            {err && (
              <div className="mt-3 p-2 rounded-sm bg-[#9E2A2B]/15 border border-[#9E2A2B]/40 text-[#FF8585] text-[12px]" data-testid="reset-error">{err}</div>
            )}
            <button type="submit" disabled={busy}
              className="mt-5 w-full bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] font-bold uppercase tracking-[0.14em] text-[12px] py-2.5 rounded-sm flex items-center justify-center gap-2"
              data-testid="reset-submit">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Updating…</> : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
